import transform from 'lodash-es/transform';
import isArray from 'lodash-es/isArray';
import isObject from 'lodash-es/isObject';
import i18n from '@/plugins/i18n';
import { date } from './vee-validate-transforms/date';
import { between } from './vee-validate-transforms/between';
import { size } from './vee-validate-transforms/size';
import { date_format } from './vee-validate-transforms/date_format';
import { other_field } from './vee-validate-transforms/other_field';
import { digits } from './vee-validate-transforms/digits';
import { values } from './vee-validate-transforms/values';
import { required_if } from './vee-validate-transforms/required_if';
import { required_unless } from './vee-validate-transforms/required_unless';
import { required_with } from './vee-validate-transforms/required_with';

function transformation_result({ name, rule, parameters }) {
  return {
    field: {
      name: name
    },
    rule: rule,
    parameters: parameters
  };
}

const merge = (error, merge = {}) => {
  const merged_error = Object.assign({}, error, merge);
  return transformation_result(merged_error);
};

function laravel_size_rules(error) {
  const { rule, parameters } = error;
  const [rule_name] = rule.split('.');
  let current_parameters = {};

  if (rule_name === 'between') {
    current_parameters = between(parameters);
  } else {
    current_parameters = size(parameters);
  }

  return merge(error, { rule: `laravel.${rule}`, parameters: current_parameters });
}

const date_rules = error => merge(error, { parameters: date(error.parameters) });
const other_field_rules = (error, fields) =>
  merge(error, { parameters: other_field(error.parameters, fields) });
const required_with_rules = (error, fields) =>
  merge(error, { parameters: required_with(error.parameters, fields) });

const map_transform = {
  between: laravel_size_rules,
  min: laravel_size_rules,
  max: laravel_size_rules,
  size: laravel_size_rules,
  lt: laravel_size_rules,
  lte: laravel_size_rules,
  gt: laravel_size_rules,
  gte: laravel_size_rules,

  after: date_rules,
  after_or_equal: date_rules,
  before: date_rules,
  before_or_equal: date_rules,
  date: date_rules,
  date_equals: date_rules,

  different: other_field_rules,
  in_array: other_field_rules,
  same: other_field_rules,

  required_with: required_with_rules,
  required_with_all: required_with_rules,
  required_without: required_with_rules,
  required_without_all: required_with_rules,

  digits_between: error => merge(error, { parameters: between(error.parameters) }),
  date_format: error => merge(error, { parameters: date_format(error.parameters) }),
  digits: error => merge(error, { parameters: digits(error.parameters) }),
  starts_with: error => merge(error, { parameters: values(error.parameters) }),
  ends_with: error => merge(error, { parameters: values(error.parameters) }),
  in: error => merge(error, { rule: 'oneOf', parameters: values(error.parameters) }),
  mimetypes: error => merge(error, { rule: 'mimes', parameters: values(error.parameters) }),
  mimes: error => merge(error, { rule: 'ext', parameters: values(error.parameters) }),
  required_if: (error, fields) =>
    merge(error, { parameters: required_if(error.parameters, fields) }),
  required_unless: (error, fields) =>
    merge(error, { parameters: required_unless(error.parameters, fields) }),
  sip_account_with_caller_id_limit: error => merge(error, { parameters: size(error.parameters) }),
  action_menu: error => merge(error, { parameters: size(error.parameters) }),
  contact_limit: error => merge(error, { parameters: size(error.parameters) }),
  contact_method_limit: error => merge(error, { parameters: size(error.parameters) })
};

function transformLaravelToVee(error, fields) {
  const [rule_name] = error.rule.split('.');
  if (map_transform[rule_name]) {
    return map_transform[rule_name](error, fields);
  }
  return transformation_result(error);
}

export function getFullObject(error, fields) {
  const two_spaces_regexp = new RegExp('\\s{2}', 'g');
  const { rule, parameters, field } = transformLaravelToVee(error, fields);

  return {
    rule: rule,
    parameters: parameters,
    message_fallback: i18n.t(`validator.rules.${rule}`, {
      _field_: `${field.name}`,
      ...parameters
    }),
    message: i18n.t(`validator.rules.${rule}`, { ...parameters }).replace(two_spaces_regexp, ' ')
  };
}

function getDeprecatedValidator(error) {
  return {
    rule: 'unknown',
    parameters: [],
    message_fallback: error,
    message: error
  };
}

export function getOnlyMessageTransform(errors) {
  return transform(errors, function(result, value, key) {
    result[key] = value.map(item => item.message);
  });
}

/**
 * Пример ответа
 * { "name":
 *  [
 *    {
 *    "message" : "The name field is required.",
 *    "rule":"required",
 *    "parameters":[]
 *    }
 *   ]
 *  }
 */
export function getErrorObjectTransform(errors, fields = {}) {
  return transform(errors, (result, value, key) => {
    const errors = isArray(value) ? value : [value];
    result[key] = errors.map(error => {
      if (isObject(error)) {
        return getFullObject({ ...error, key: key, name: fields[key] || key }, fields);
      } else {
        console.warn('A deprecated validator is used.');
        return getDeprecatedValidator(error);
      }
    });
  });
}
